import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, Copy, ExternalLink, Paperclip, RotateCcw, Send, Sparkles, X } from 'lucide-react';

import { useAdmin } from '@lok/context/AdminContext';
import { useAskAssistantMutation, useAssistantStatusQuery } from '@lok/services/learner';

// CON-306 Course Assistant: the floating chat from the Figma Make
// prototype, answered by the backend (Claude). Shown only when the backend
// reports the assistant as available (an API key is configured). The
// conversation lives in this component only; nothing is saved. The
// learner's uploads are sent only for a question where they tick
// "Include my uploads" (or pick the feedback suggestion); the box clears
// after each question.

// Course questions: the assistant answers from the learner's own Continuum
// courses first.
const SUGGESTIONS = [
  { text: 'What should I work on next in my courses?' },
  { text: 'How do I prepare for customer discovery interviews?' },
  { text: 'How do I size my market opportunity (TAM/SAM/SOM)?' },
  { text: 'Can you give me feedback on my latest deliverable?', includeFiles: true }
];

// Minimal Markdown for the assistant's replies: headings, bullet and
// numbered lists, **bold**, `code` and links: Continuum lessons
// ([title](/modules/...)) open inside the platform; web links (which the
// backend limits to lesson resources and approved sites) open in a new tab;
// anything else shows as plain text. Everything is rendered as React text,
// never as HTML.
const INLINE = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`)/g;
// Same rule as the backend: copied lessons carry "|" and other punctuation
// in their slugs, so any path characters except whitespace, brackets,
// quotes and a scheme's colon.
const PLATFORM_PATH = /^\/(modules|courses)\/[^\s()<>"'`:]+$/;
const WEB_URL = /^https?:\/\/[^\s()<>"'`]+$/i;

const inline = (raw, keyBase) => {
  const parts = [];
  let last = 0;
  let i = 0;
  let m;
  // A fresh regex per call: bold text is formatted recursively (models
  // often bold a lesson link), and a shared global regex would lose its place.
  const re = new RegExp(INLINE.source, 'g');
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) parts.push(raw.slice(last, m.index));
    const key = `${keyBase}-${i}`;
    i += 1;
    if (m[2]) {
      parts.push(<strong key={key} className='font-semibold text-gray-900 dark:text-white'>{inline(m[2], key)}</strong>);
    } else if (m[3]) {
      let node = m[3];
      if (PLATFORM_PATH.test(m[4])) {
        node = (
          <Link key={key} to={m[4]} className='text-primary-green underline underline-offset-2 hover:opacity-80'>
            {m[3]}
          </Link>
        );
      } else if (WEB_URL.test(m[4])) {
        // The backend keeps only lesson resources and approved sites.
        node = (
          <a
            key={key}
            href={m[4]}
            target='_blank'
            rel='noopener noreferrer'
            className='text-primary-green underline underline-offset-2 hover:opacity-80 inline-flex items-center gap-0.5'
          >
            {m[3]}
            <ExternalLink className='w-2.5 h-2.5' />
          </a>
        );
      }
      parts.push(node);
    } else if (m[5]) {
      parts.push(<code key={key} className='text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono'>{m[5]}</code>);
    }
    last = m.index + m[0].length;
  }
  if (last < raw.length) parts.push(raw.slice(last));
  return parts;
};

const renderMarkdown = (text) => {
  const nodes = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      nodes.push(<ul key={`ul-${nodes.length}`} className='space-y-1 my-1.5'>{list}</ul>);
      list = [];
    }
  };
  text.split('\n').forEach((line, idx) => {
    const key = `l-${idx}`;
    if (/^#{1,4} /.test(line)) {
      flush();
      nodes.push(<p key={key} className='text-sm font-semibold text-gray-900 dark:text-white mt-2 mb-1'>{inline(line.replace(/^#{1,4} /, ''), key)}</p>);
    } else if (/^\s*[-*] /.test(line)) {
      list.push(
        <li key={key} className='flex gap-2 text-sm leading-relaxed'>
          <span className='text-primary-green flex-shrink-0'>•</span>
          <span>{inline(line.replace(/^\s*[-*] /, ''), key)}</span>
        </li>
      );
    } else if (/^\d+\. /.test(line)) {
      const num = line.match(/^(\d+)\. /)[1];
      list.push(
        <li key={key} className='flex gap-2 text-sm leading-relaxed'>
          <span className='text-primary-green font-semibold flex-shrink-0 w-4 text-right'>{num}.</span>
          <span>{inline(line.replace(/^\d+\. /, ''), key)}</span>
        </li>
      );
    } else if (!line.trim()) {
      flush();
      nodes.push(<div key={key} className='h-1.5' />);
    } else {
      flush();
      nodes.push(<p key={key} className='text-sm leading-relaxed'>{inline(line, key)}</p>);
    }
  });
  flush();
  return nodes;
};

const CourseAssistant = () => {
  // Super admins also have the agent dock's "Ask the agent" button in the
  // same corner; sit above it for them.
  const { isSuperAdmin } = useAdmin();
  const { data: status } = useAssistantStatusQuery();
  const [ask, { isLoading }] = useAskAssistantMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [remaining, setRemaining] = useState(null);
  const [copied, setCopied] = useState(null);
  const [includeFiles, setIncludeFiles] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  if (!status?.available) return null;
  const left = remaining ?? status.remaining;

  const send = async (text, withFiles = includeFiles) => {
    const question = text.trim();
    if (!question || isLoading) return;
    const next = [...messages, { role: 'user', content: question, withFiles }];
    setMessages(next);
    setInput('');
    setIncludeFiles(false);
    try {
      const history = next.filter((m) => !m.error).map(({ role, content }) => ({ role, content }));
      const res = await ask({ messages: history, includeFiles: withFiles }).unwrap();
      setMessages([...next, { role: 'assistant', content: res.reply }]);
      setRemaining(res.remaining);
    } catch (err) {
      const detail = err?.detail || 'The assistant is not available right now. Please try again later.';
      // 429 is also the per-minute limit; only the daily limit says remaining 0.
      if (err?.status === 429 && err?.data?.remaining === 0) setRemaining(0);
      setMessages([...next, { role: 'assistant', content: detail, error: true }]);
    }
  };

  return (
    <>
      <button
        type='button'
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Close course assistant' : 'Open course assistant'}
        aria-expanded={isOpen}
        className={`fixed ${isSuperAdmin ? 'bottom-24 right-6' : 'bottom-5 right-5'} z-50 w-[52px] h-[52px] rounded-full shadow-xl flex items-center justify-center transition-all ${
          isOpen ? 'bg-gray-800 dark:bg-gray-700' : 'bg-primary-green hover:bg-primary-green/90 hover:scale-105'
        }`}
      >
        {isOpen ? <X className='w-5 h-5 text-white' /> : <Sparkles className='w-5 h-5 text-primary-green-fg' />}
      </button>

      {isOpen && (
        <div
          role='dialog'
          aria-label='Course assistant'
          className={`fixed ${isSuperAdmin ? 'bottom-40 right-6' : 'bottom-20 right-5'} z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[calc(100vh-11rem)] bg-white dark:bg-[#141C27] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
        >
          <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0'>
            <span className='w-8 h-8 rounded-lg bg-primary-green/15 flex items-center justify-center'>
              <Sparkles className='w-4 h-4 text-primary-green' />
            </span>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-semibold text-gray-900 dark:text-white'>Course Assistant</p>
              <p className='text-xs text-gray-400'>Your courses · Next steps · Feedback</p>
            </div>
            {messages.length > 0 && (
              <button
                type='button'
                onClick={() => setMessages([])}
                aria-label='Clear chat'
                title='Clear chat'
                className='p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              >
                <RotateCcw className='w-3.5 h-3.5' />
              </button>
            )}
          </div>

          <div className='flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0' aria-live='polite'>
            {messages.length === 0 && (
              <div className='space-y-3'>
                <p className='flex items-center gap-2 justify-center pt-1 text-xs text-gray-500 dark:text-gray-400'>
                  <BookOpen className='w-4 h-4 text-primary-green' /> Ask about your Continuum courses
                </p>
                <div className='space-y-1.5'>
                  {SUGGESTIONS.map((q) => (
                    <button
                      key={q.text}
                      type='button'
                      onClick={() => send(q.text, !!q.includeFiles)}
                      className='w-full text-left px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1D2838] text-xs text-gray-600 dark:text-gray-300 hover:border-primary-green/60'
                    >
                      {q.text}
                      {q.includeFiles && (
                        <span className='mt-0.5 flex items-center gap-1 text-[10px] text-gray-400'>
                          <Paperclip className='w-2.5 h-2.5' /> Shares your recent uploads with the assistant
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, idx) =>
              msg.role === 'user' ? (
                // eslint-disable-next-line react/no-array-index-key
                <div key={idx} className='flex flex-col items-end gap-0.5'>
                  <div className='max-w-[85%] bg-primary-green text-primary-green-fg rounded-2xl rounded-tr-sm px-3 py-2 text-sm font-medium whitespace-pre-wrap'>
                    {msg.content}
                  </div>
                  {msg.withFiles && (
                    <span className='flex items-center gap-1 text-[10px] text-gray-400'>
                      <Paperclip className='w-2.5 h-2.5' /> With your recent uploads
                    </span>
                  )}
                </div>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <div key={idx} className='flex justify-start'>
                  <div className='max-w-[92%] group relative'>
                    <div
                      className={`border rounded-2xl rounded-tl-sm px-3 py-2.5 text-gray-700 dark:text-gray-300 ${
                        msg.error
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                          : 'bg-gray-50 dark:bg-[#1D2838] border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {renderMarkdown(msg.content)}
                    </div>
                    {!msg.error && (
                      <button
                        type='button'
                        aria-label='Copy answer'
                        onClick={() => {
                          navigator.clipboard?.writeText(msg.content);
                          setCopied(idx);
                          setTimeout(() => setCopied(null), 1500);
                        }}
                        className='absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-primary-green'
                      >
                        {copied === idx ? <Check className='w-3 h-3' /> : <Copy className='w-3 h-3' />}
                      </button>
                    )}
                  </div>
                </div>
              )
            )}
            {isLoading && (
              <div className='flex justify-start'>
                <div className='bg-gray-50 dark:bg-[#1D2838] border border-gray-200 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center' aria-label='Assistant is typing'>
                  {[0, 1, 2].map((i) => (
                    <span key={i} className='w-1.5 h-1.5 rounded-full bg-primary-green animate-bounce' style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className='px-3 py-3 border-t border-gray-100 dark:border-gray-700 flex-shrink-0'>
            <label className='mb-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none'>
              <input
                type='checkbox'
                checked={includeFiles}
                disabled={isLoading || left === 0}
                onChange={(e) => setIncludeFiles(e.target.checked)}
                className='rounded border-gray-300 dark:border-gray-600 text-primary-green focus:ring-primary-green'
              />
              <Paperclip className='w-3 h-3' />
              Include my uploads (up to 3 recent files) with this question
            </label>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className='flex items-end gap-2'
            >
              <textarea
                ref={inputRef}
                value={input}
                rows={1}
                maxLength={2000}
                disabled={isLoading || left === 0}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                placeholder={left === 0 ? "You've reached today's limit" : 'Ask about your courses…'}
                aria-label='Ask the course assistant'
                className='flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1D2838] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-green resize-none disabled:opacity-50 max-h-20'
              />
              <button
                type='submit'
                aria-label='Send'
                disabled={!input.trim() || isLoading || left === 0}
                className='flex-shrink-0 w-9 h-9 rounded-xl bg-primary-green text-primary-green-fg flex items-center justify-center disabled:opacity-40'
              >
                <Send className='w-4 h-4' />
              </button>
            </form>
            <p className='mt-1.5 text-[10px] text-gray-400 text-center'>
              AI answers can be wrong; check important details. {left != null ? `${left} questions left today.` : ''}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseAssistant;
